import MessageFormat from '@messageformat/core';

import type { TranslationCatalog } from '../types/translation-catalog';

/**
 * Options for translateI18nKey
 */
interface TranslateI18nKeyOptions {
	/**
	 * The active locale. Null, undefined, empty and whitespace-only
	 * values collapse to "en"
	 */
	locale?: string | null;
	/**
	 * Variables made available to ICU interpolation
	 */
	vars?: Record<string, unknown>;
}

const FALLBACK_LOCALE = 'en';

/**
 * Coerce an arbitrary locale value into a non-empty iso code, falling
 * back to "en" so downstream catalog and MessageFormat lookups always
 * have a valid locale to work with
 */
function normalizeLocale(value: string | null | undefined): string {
	if (value === null || value === undefined) {
		return FALLBACK_LOCALE;
	}

	const trimmed = value.trim();

	return trimmed.length > 0 ? trimmed : FALLBACK_LOCALE;
}

function catalogForLocale(
	i18n: readonly TranslationCatalog[] | undefined,
	locale: string,
) {
	return i18n?.find(
		(translationCatalog) => translationCatalog.locale === locale,
	)?.entry;
}

/**
 * Resolve the ICU template string for a key: requested locale catalog,
 * then `en`, then give up so the caller can return the raw key.
 */
function resolveTemplate(
	i18n: readonly TranslationCatalog[] | undefined,
	locale: string,
	key: string,
): { template: string; messageFormatLocale: string } | null {
	if (!key || !i18n?.length) {
		return null;
	}

	const primary = catalogForLocale(i18n, locale)?.[key];

	if (primary !== undefined && primary !== null) {
		return { template: primary, messageFormatLocale: locale };
	}

	const english = catalogForLocale(i18n, FALLBACK_LOCALE)?.[key];

	if (english !== undefined && english !== null) {
		return { template: english, messageFormatLocale: FALLBACK_LOCALE };
	}

	return null;
}

/**
 * Translate a flat, pipe-delimited key against translation catalog entries.
 *
 * Server-side counterpart of the Handlebars `translate` helper used in
 * report templates. Each item is one `TranslationCatalog` (a loaded
 * `{locale}.json` under `api/src/assets/i18n/`).
 *
 * Lookup order for the template string: catalog for the requested locale,
 * then the `en` catalog, then the raw key is returned unchanged.
 *
 * @example
 * translateI18nKey(
 *   'notifications|subject|rental_agreement|created',
 *   i18n,
 *   { locale: 'el', vars: { customerName: 'Acme' } },
 * );
 */
export function translateI18nKey(
	key: string | undefined | null,
	i18n: readonly TranslationCatalog[] | undefined,
	options?: TranslateI18nKeyOptions,
): string {
	if (!key) {
		return '';
	}

	const locale = normalizeLocale(options?.locale);
	const resolved = resolveTemplate(i18n, locale, key);

	if (!resolved) {
		return key;
	}

	const formatter = new MessageFormat(resolved.messageFormatLocale).compile(
		resolved.template,
	);

	return formatter(options?.vars ?? {});
}
